<template>
  <b-modal
    id="make-payment"
    ref="make-payment"
    centered
    title="Make a payment"
    @show="setModalData"
    @hidden="resetModal"
  >
    <b-overlay
      id="overlay-background"
      :show="isLoading"
      variant="white"
      opacity="1"
      rounded="sm"
    >
      <b-row
        v-if="!showPaymentDetails"
        class="p-2"
      >
        <b-col
          cols="12"
          class="mb-3"
        >
          <b-form-group
            label="Application selection"
          >
            <v-select
              v-model="selectedApplication"
              :options="applicationsList"
              label="user_full_name"
              class="customSelect"
              placeholder="Please Select Application"
              :clearable="false"
              @input="selectApp"
            >
              <template
                #selected-option="{ program_name, user_full_name }"
              >
                <span>{{ user_full_name }}</span>&nbsp;-&nbsp;<span>{{ program_name }}</span>
              </template>

              <template
                #option="{ program_name, user_full_name }"
              >
                <span>{{ user_full_name }}</span> - <span>{{ program_name }}</span>
              </template>
            </v-select>
          </b-form-group>
        </b-col>

        <!-- PC reminder notification -->
        <b-col
          v-if="needsPcReminder"
          cols="12"
          class="mb-3"
        >
          <div class="pc-reminder">
            <span class="pc-reminder__icon">!</span>
            <span class="pc-reminder__text">Payment contract not selected.</span>
            <router-link
              :to="pcStepRoute"
              class="pc-reminder__link"
              @click.native="$bvModal.hide('make-payment')"
            >
              Go to payment option step →
            </router-link>
          </div>
        </b-col>

        <b-col
          v-if="chargesListWithState.length > 0"
          cols="12"
          class="mb-3"
        >
          <div
            class="d-flex justify-content-between bg-light py-1 px-3 mb-2 rounded-lg"
          >
            <span>Charges information</span><span>Pay amount</span>
          </div>
          <div
            v-for="charge in chargesListWithState"
            :key="charge.id"
          >
            <div class="d-flex align-items-center mb-1 w-100 charge px-2">
              <b-form-checkbox
                v-model="charge.selected"
                class="mr-1"
                :disabled="charge.charge_remaining_sum === 0"
                @change="onChargeToggle(charge)"
              />
              <span class="flex-grow-1" v-if="charge.description">{{ charge.description }}</span>
              <span class="flex-grow-1" v-else>null</span>
              <div class="charge-amount-input ml-2">
                <b-input-group prepend="$" size="sm">
                  <b-form-input
                    v-model.number="charge.pay_sum"
                    type="number"
                    size="sm"
                    :min="0"
                    :max="charge.charge_remaining_sum"
                    :class="{ 'is-invalid': charge.selected && charge.pay_sum > charge.charge_remaining_sum }"
                    :disabled="!charge.selected"
                    style="width: 90px;"
                    @input="onChargeAmountInput(charge)"
                    @blur="onChargeAmountBlur(charge)"
                  />
                </b-input-group>
                <small v-if="charge.pay_sum > charge.charge_remaining_sum" class="text-danger d-block text-right">
                  Max: {{ charge.charge_remaining_sum | fixAmount }}
                </small>
              </div>
            </div>
            <hr>
          </div>
          <small v-if="chargesAmountError" class="text-danger">
            {{ chargesAmountError }}
          </small>
        </b-col>

        <b-col
          v-if="paymentMethod && (paymentMethod !== 'Credit Card' || selectedCard) && applicableFees.length > 0"
          cols="12"
          class="mb-3"
        >
          <div
            v-for="fee in applicableFees"
            :key="fee.id"
            class="d-flex justify-content-between mb-1 px-2"
          >
            <span class="text-muted">{{ fee.name }}</span>
            <span class="text-muted">{{ fee.calculatedAmount | fixAmount }}</span>
          </div>
        </b-col>

        <b-col
          v-if="!needsPcReminder"
          cols="12"
          class="mb-3"
        >
          <label
            class="form__row"
          >
            <span class="form__row-label">Amount $</span>
            <input
              :value="paymentAmount ? Number(paymentAmount).toFixed(2) : '0.00'"
              type="text"
              class="input"
              readonly
              style="background: #f8f8f8; cursor: default;"
            />
          </label>
          <p
            v-if="isAmountMoreThenTotal"
            class="text-primary"
          >
            *The amount entered is more than application total amount
          </p>
          
        </b-col>
        
        <b-col
          v-if="!needsPcReminder"
          cols="12"
          class="mb-3"
        >
          <b-form-group
            label="Select Payment Method"
          >
            <v-select
              v-model="paymentMethod"
              :options="paymentMethodsList"
              class="customSelect"
              placeholder="Please Select Payment Method"
              :clearable="false"
              :searchable="false"
              :selectable="isOptionSelectable"
            >
              <template
                #selected-option="{ label }"
              >
                <div
                  v-if="label == 'Cash Bank'"
                >
                  <span>{{ label }}</span> <span>({{ cashBankBalance | fixAmount }})</span>
                </div>

                <div v-else>
                  {{ label }}
                </div>
              </template>

              <template
                #option="{ label }"
              >
                <div
                  v-if="label == 'Cash Bank'"
                  class="d-flex justify-content-between"
                >
                  <span>{{ label }}</span><span>{{ cashBankBalance | fixAmount }}</span>
                </div>
                <div v-else>
                  {{ label }}
                </div>
              </template>
            </v-select>
          </b-form-group>
        </b-col>

        <b-col
          v-if="paymentMethod == 'Credit Card' && !needsPcReminder"
          cols="12"
          class="mb-1"
        >
          <b-form-group label="Select Credit Card">
            <CreditCardRadioBlock 
              v-for="card in cardsList"
              :key="card.id"
              :cards-list="cardsList"
              :card="card"
            />
            <b-button
              variant="primary"
              class="w-100 mt-2"
              v-b-modal.add-new-card
            >
              Add New Credit Card +
            </b-button>
          </b-form-group>
        </b-col>

        <b-col
          v-if="paymentMethod !== 'Cash Bank' && cashBankBalance && cashBankBalance < paymentAmount"
          cols="12"
        >
          <b-form-checkbox
            v-model="includeCashBank"
            class="mr-1 bulk-checkbox"
          >
           Include CashBank amount ({{ cashBankBalance | fixAmount}}) in payment
          </b-form-checkbox>
        </b-col>

        <b-col
          v-if="errorMessage"
          cols="12"
          class="mb-1"
        >
          <p
            class="text-danger text-center"
          >
            {{ errorMessage }}
          </p>
        </b-col>
      </b-row>

      <PaymentInformation
        v-if="showPaymentDetails"
      />
    </b-overlay>
    <template #modal-footer="{ cancel }">
      <div
        v-if="!showPaymentDetails"
        class="w-100 d-flex justify-content-between"
      >
        <b-button
          class="border border-primary"
          variant="outline-primary" 
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="submitButtonIsLoading || !isMakePaymentEnabled || needsPcReminder"
          @click="showPaymentDetails = true"
        >
          <span>Make Payment</span>
        </b-button>
      </div>
      <div
        v-else
        class="w-100 d-flex justify-content-between"
      >
        <b-button
          class="border border-primary"
          variant="outline-primary" 
          @click="showPaymentDetails = false"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="submitButtonIsLoading || !isMakePaymentEnabled"
          @click="handleForm"
        >
          <span>Submit Payment</span>
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { getUserData } from '@/auth/utils'
import { mapGetters } from 'vuex'

import {
  BModal, BRow, BCol, BFormGroup, 
  VBModal, BButton, BOverlay, BFormCheckbox, BFormInput, BInputGroup,
} from 'bootstrap-vue'

import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

import PaymentInformation from '@/views/PaymentPortal/Modals/Payment/PaymentInformation.vue'
import CreditCardRadioBlock from '@/components/site/CreditCardRadioBlock.vue'

import { filters } from '@core/mixins/filters'
import { setStatusModalData } from '@core/mixins/setStatusModalData'
import { calculateApplicableFees } from '@/utils/payment'

export default {
  components: {
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BOverlay,
    BFormCheckbox,
    BFormInput,
    BInputGroup,

    vSelect,

    PaymentInformation,
    CreditCardRadioBlock,
  },
  mixins: [ filters, setStatusModalData ],
  directives: {
    'b-modal': VBModal,
  },
  data() {
    return {
      userId: getUserData().id,
      errorMessage: '',
      submitButtonIsLoading: false,
      showPaymentDetails: false,
      chargesListWithState: [],
    }
  },
  computed: {
    ...mapGetters({
      applicationsList: 'paymentPortal/getApplications',
      chargesList: 'paymentPortal/getChargesList',
      paymentMethodsList: 'paymentPortal/getPaymentMethodsList',
      cardsList: 'cards/getCardsList',
      selectedCardInfo: 'cards/getSelectedCardInfo',
      cardForRemove: 'cards/getCardForRemove',
      isLoading: 'paymentPortal/getIsModalLoaidng',
      cashBankBalance: 'paymentPortal/getCashBankBalance',
      paymentFees: 'paymentPortal/getPaymentFees',
    }),
    selectedApplication: {
      get: function () {
        return this.$store.getters['paymentPortal/getSelectedApplication']
      },
      set: function (val) {
        this.$store.commit('paymentPortal/SET_SLECTED_APPLICATION', val)
      }
    },
    paymentAmount: {
      get: function () {
        return this.$store.getters['paymentPortal/getPaymentAmount']
      },
      set: function (val) {
        this.$store.commit('paymentPortal/SET_PAYMENT_AMOUNT', val)
      }
    },
    paymentMethod: {
      get: function () {
        return this.$store.getters['paymentPortal/getPaymentMethod']
      },
      set: function (val) {
        this.$store.commit('paymentPortal/SET_PAYMENT_METHOD', val)
      }
    },
    includeCashBank: {
      get: function () {
        return this.$store.getters['paymentPortal/getIncludeCashBank']
      },
      set: function (val) {
        this.$store.commit('paymentPortal/SET_INCLUDE_CASH_BANK_STATUS', val)
      }
    },
    selectedCard: {
      get: function () {
        return this.$store.getters['cards/getSelectedCreditCard']
      },
      set: function (val) {
        this.$store.commit('cards/SET_SELECTED_CREDIT_CARD', val)
      }
    },
    isModalLoading: {
      get: function () {
        return this.$store.getters['paymentPortal/getIsModalLoaidng']
      },
      set: function (val) {
        this.$store.commit('paymentPortal/SET_MODAL_LOADING_STATE', val)
      },
    },
    selectedChargesWithSum() {
      return this.chargesListWithState.filter(c => c.selected)
    },
    selectedChargesTotal() {
      return this.selectedChargesWithSum.reduce((acc, c) => acc + Number(c.pay_sum || 0), 0)
    },
    chargesAmountError() {
      const hasOverpayment = this.chargesListWithState.some(c => c.pay_sum > c.charge_remaining_sum)
      if (hasOverpayment) return 'One or more charge amounts exceed the maximum allowed.'
      if (this.selectedChargesWithSum.length === 0) return 'Please select at least one charge.'
      return null
    },
    isMakePaymentEnabled() {
      if (!this.selectedApplication?.id || !this.paymentAmount || !this.paymentMethod) return false
      if (this.chargesListWithState.length > 0) {
        if (this.chargesAmountError) return false
        if (this.selectedChargesWithSum.length === 0) return false
      }
      if (this.paymentMethod === 'Credit Card' && !this.selectedCard) return false
      return true
    },
    totalChargesAmount() {
      return this.chargesList.reduce((acc, curItem) => acc + Number(curItem.charge_remaining_sum), 0)
    },
    effectivePaymentMethod() {
      if (this.paymentMethod === 'Credit Card' && this.selectedCardInfo) {
        return this.selectedCardInfo.type?.toLowerCase() === 'ach' ? 'ACH' : 'Credit Card'
      }
      return this.paymentMethod
    },
    applicableFees() {
      return calculateApplicableFees(this.paymentFees, this.effectivePaymentMethod, this.selectedChargesTotal || this.totalChargesAmount)
    },
    isAmountMoreThenTotal() {
      return this.paymentAmount > this.totalChargesAmount
    },
    /**
     * True when:
     *  - the school setting PaymentsRequirePC is enabled
     *  - an application is selected
     *  - application is accepted
     *  - the form has a PC step (has_pc_step)
     *  - but no PC has been chosen yet (!has_pc)
     */
    needsPcReminder() {
      if (!this.$ability.can('manage', 'PaymentsRequirePC')) return false
      const app = this.selectedApplication
      if (!app) return false
      return app.is_accepted && app.has_pc_step && !app.has_pc
    },
    /**
     * Route to the payment-contract step for the selected application.
     */
    pcStepRoute() {
      const app = this.selectedApplication
      if (!app) return {}
      return {
        name: 'application-create',
        params: { id: app.program_id },
        query: {
          app_id: app.id,
          user_id: app.user_id,
          navigate_to_pc: 1,
        },
      }
    },
  },
  watch: {
    chargesList(newList) {
      this.initChargesListWithState(newList)
    },
    selectedChargesTotal(val) {
      this.paymentAmount = Math.round(val * 100) / 100
    },
  },
  methods: {
    initChargesListWithState(list) {
      this.chargesListWithState = list.map(charge => ({
        ...charge,
        selected: charge.charge_remaining_sum > 0,
        pay_sum: charge.charge_remaining_sum,
      }))
      this.syncSelectedChargesToStore()
    },
    syncSelectedChargesToStore() {
      this.$store.commit('paymentPortal/SET_SELECTED_CHARGES', this.chargesListWithState.filter(c => c.selected))
    },
    onChargeToggle(charge) {
      if (!charge.selected) {
        charge.pay_sum = 0
      } else {
        charge.pay_sum = charge.charge_remaining_sum
      }
      this.syncSelectedChargesToStore()
    },
    onChargeAmountInput(charge) {
      // Auto-check the charge if user types a value
      if (Number(charge.pay_sum) > 0 && !charge.selected) {
        charge.selected = true
      }
      if (Number(charge.pay_sum) === 0) {
        charge.selected = false
      }
      this.syncSelectedChargesToStore()
    },
    onChargeAmountBlur(charge) {
      // Clamp value to max
      if (charge.pay_sum > charge.charge_remaining_sum) {
        charge.pay_sum = charge.charge_remaining_sum
      }
      if (charge.pay_sum < 0 || !charge.pay_sum) {
        charge.pay_sum = 0
        charge.selected = false
      }
      this.syncSelectedChargesToStore()
    },
    prepareChargesPayload() {
      return this.selectedChargesWithSum.map(c => ({
        id: c.charge_id,
        pay_sum: Number(c.pay_sum),
      }))
    },
    async setModalData() {
      this.isModalLoading = true
      await this.$store.dispatch('cards/loadCards')
      await this.$store.dispatch('paymentPortal/fetchApplications', { without_archived: false })
      this.isModalLoading = false
    },
    resetModal() {
      this.$store.commit('paymentPortal/SET_PAYMENT_METHOD', 'Credit Card')
      this.$store.commit('paymentPortal/SET_PAYMENT_AMOUNT', 0)
      this.$store.commit('paymentPortal/SET_CHARGES_LIST', [])
      this.$store.commit('paymentPortal/SET_INCLUDE_CASH_BANK_STATUS', false)
      this.$store.commit('paymentPortal/SET_PAYMENT_FEES', [])
      this.selectedApplication = null
      this.selectedCard = null
      this.showPaymentDetails = false
      this.chargesListWithState = []
      this.$store.commit('paymentPortal/SET_SELECTED_CHARGES', [])
    },
    selectApp(application) {
      this.selectedCard = application.card_id
      this.updateTransactionChargesList(application.id, application.program_id)
    },
    async updateTransactionChargesList(applicationId, programId) {
      this.isModalLoading = true

      const queryParams = {
        applicationId,
        is_without_charge: 0,
      }
      await this.$store.dispatch('paymentPortal/fetchTransactionCharges', queryParams)

      if (programId) {
        await this.$store.dispatch('paymentPortal/fetchPaymentFees', programId)
      }

      this.setPaymentAmount()

      this.isModalLoading = false
    },
    setPaymentAmount() {
      this.$store.commit('paymentPortal/SET_PAYMENT_AMOUNT', this.totalChargesAmount)
    },
    async handleForm(bvModalEvt) {
      bvModalEvt.preventDefault()

      if(this.includeCashBank) {
        await this.createTransactionIncludeCashBank()
      } else {
        await this.createTransaction()
      }
    },
    async createTransaction() {
      this.submitButtonIsLoading = true
      this.isModalLoading = true

      const queryParams = {
        user_id: this.userId,
        application_id: this.selectedApplication?.id,
        amount: this.paymentAmount,
        type: this.paymentMethod,
        charges: this.prepareChargesPayload(),
        ...(this.paymentMethod === 'Credit Card' && { card_id: this.selectedCard }),
      }

      try {
        await this.$store.dispatch('paymentPortal/sendNewTransaction', queryParams)

        this.showStatusModal({
          title: 'Make payment',
          status: 'Success',
          header: 'Your payment has been sent',
          message: '',
        })

        await this.$store.dispatch('paymentPortal/refreshData')
      } catch (e) {
        this.showStatusModal({
          title: 'Make a payment',
          status: 'Failed',
          header: 'Your payment failed',
          message: 'You need to check if there is money on the card (or contact the bank about the error)',
        })
      } finally {
        this.submitButtonIsLoading = false
        this.isModalLoading = false
      }
    },
    showStatusModal(data) {
      this.setStatusModalData(data)
      this.$bvModal.hide('make-payment')
      this.$bvModal.show('status-modal')
    },
    async createTransactionIncludeCashBank() {
      this.submitButtonIsLoading = true
      this.isModalLoading = true

      const chargesPayload = this.prepareChargesPayload()

      const mainPaymentQueryParams = {
        user_id: this.userId,
        application_id: this.selectedApplication?.id,
        charges: chargesPayload,
        amount: this.paymentAmount - this.cashBankBalance,
        type: this.paymentMethod,
        ...(this.paymentMethod === 'Credit Card' && { card_id: this.selectedCard }),
      }

      const cashBankPaymentQueryParams = {
        user_id: this.userId,
        application_id: this.selectedApplication?.id,
        charges: chargesPayload,
        amount: this.cashBankBalance,
        type: 'Cash Bank',
      }

      try {
        // 1. create main transaction
        await this.$store.dispatch('paymentPortal/sendNewTransaction', mainPaymentQueryParams)

        // 2. create Cash Bank transaction
        await this.$store.dispatch('paymentPortal/sendNewTransaction', cashBankPaymentQueryParams)

        this.showStatusModal({
          title: 'Make payment',
          status: 'Success',
          header: 'Your payment has been sent',
          message: '',
        })

        this.$store.dispatch('paymentPortal/fetchTransactionsList')
        this.$store.dispatch('paymentPortal/fetchUpcomingPayments')
        this.$store.dispatch('paymentPortal/fetchCashBankBalance')
      } catch (e) {
        this.showStatusModal({
          title: 'Make a payment',
          status: 'Failed',
          header: 'Your payment failed',
          message: 'The CC was not charged, try again later or use another credit card',
        })
      } finally {
        this.submitButtonIsLoading = false
        this.isModalLoading = false
      }
    },
    isOptionSelectable(option){
      return option == 'Credit Card' || this.paymentAmount <= this.cashBankBalance
    },
  },
}
</script>
<style lang="scss">
.charge {
  .custom-control-label {
    width: 100%;
    padding-right: 20px;
  }
}

.charge-amount-input {
  flex-shrink: 0;

  .input-group {
    flex-wrap: nowrap;
  }

  // Force the input-group border to always render correctly
  // Bootstrap-Vue can drop borders when :state changes on b-form-input inside input-group
  .input-group > .form-control {
    border: 1px solid #d8d6de;
    border-left: 0;

    &.is-invalid {
      border-color: #ea5455;
    }

    &:focus {
      border-color: #7367f0;
      box-shadow: none;
    }

    &:disabled {
      background-color: #efefef;
      border-color: #d8d6de;
    }
  }

  input[type="number"] {
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}

.pc-reminder {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border: 1px solid $primary;
  border-radius: 10px;
  background-color: rgba($secondary_bg, 0.15);
  font-size: 14px;
  color: $primary;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: $primary;
    color: #fff;
    font-weight: 700;
    font-size: 13px;
    line-height: 1;
  }

  &__text {
    flex: 1;
    font-weight: 600;
    color: $primary;
  }

  &__link {
    flex-shrink: 0;
    font-weight: 700;
    color: $primaryButton;
    text-decoration: none;
    border-bottom: 1px solid $primaryButton;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.75;
      text-decoration: none;
    }
  }
}
</style>